import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Lock, CheckCircle2, Loader2, CreditCard, AlertCircle,
} from "lucide-react";
import SignaturePad from "../components/SignaturePad";
import { detectBrand, BrandBadge } from "../components/CardBrand";
import api from "../lib/api";

const YEARS = Array.from({ length: 12 }, (_, i) => String(new Date().getFullYear() + i).slice(2));
const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));

export default function PublicSign() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | done | notfound
  const [busy, setBusy] = useState(false);
  const [verId, setVerId] = useState("");

  const [card, setCard] = useState({ number: "", cardholder_name: "", exp_month: "", exp_year: "", billing_zip: "" });
  const [sig, setSig] = useState({ type: "draw", data: "" });
  const [agree, setAgree] = useState(false);

  const brand = detectBrand(card.number);
  const money = (n, c) => new Intl.NumberFormat("en-US", { style: "currency", currency: c || "USD" }).format(n || 0);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/forms/${token}`);
        setForm(res.data);
        setState(res.data.status === "signed" ? "done" : (res.data.locked ? "locked" : "ready"));
        if (res.data.card) setVerId("");
      } catch (e) {
        setState("notfound");
      }
    })();
  }, [token]);

  const fmtCard = (v) => v.replace(/\D/g, "").slice(0, 16).replace(/(\d{4})(?=\d)/g, "$1 ").trim();

  const submit = async () => {
    if (!card.cardholder_name.trim()) return toast.error("Enter the cardholder name");
    const digits = card.number.replace(/\D/g, "");
    if (digits.length < 13) return toast.error("Enter a valid card number");
    if (!card.exp_month || !card.exp_year) return toast.error("Select the card expiration");
    if (!sig.data) return toast.error("Please provide your signature");
    if (!agree) return toast.error("You must agree to the authorization");

    setBusy(true);
    try {
      const res = await api.post(`/public/forms/${token}/sign`, {
        signature_type: sig.type,
        signature_data: sig.data,
        agree,
        card: {
          cardholder_name: card.cardholder_name,
          brand,
          last4: digits.slice(-4),
          exp_month: card.exp_month,
          exp_year: card.exp_year,
          billing_zip: card.billing_zip,
        },
      });
      setVerId(res.data.verification_id);
      setState("done");
      toast.success("Authorization signed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") {
    return <Center><div className="h-9 w-9 rounded-full border-2 border-[#E2E8F0] border-t-[#2563EB] animate-spin" /></Center>;
  }

  if (state === "notfound") {
    return (
      <Center>
        <div className="text-center max-w-sm">
          <span className="mx-auto h-12 w-12 rounded-xl bg-[#FEF2F2] flex items-center justify-center"><AlertCircle size={22} className="text-[#DC2626]" /></span>
          <h1 className="mt-4 text-xl font-bold text-[#0F172A]">Form not found</h1>
          <p className="text-sm text-[#64748B] mt-1">This authorization link is invalid or has expired. Please contact the merchant for a new link.</p>
        </div>
      </Center>
    );
  }

  if (state === "locked") {
    return (
      <Center>
        <div data-testid="public-sign-locked" className="text-center max-w-sm">
          <span className="mx-auto h-12 w-12 rounded-xl bg-[#FFFBEB] flex items-center justify-center"><Lock size={22} className="text-[#D97706]" /></span>
          <h1 className="mt-4 text-xl font-bold text-[#0F172A]">Form temporarily unavailable</h1>
          <p className="text-sm text-[#64748B] mt-1">This authorization is not currently active. Please contact the business that sent it to you.</p>
        </div>
      </Center>
    );
  }

  if (state === "done") {
    return (
      <Center>
        <div data-testid="signing-success-message" className="text-center max-w-md animate-rise">
          <span className="mx-auto h-16 w-16 rounded-2xl bg-[#ECFDF5] flex items-center justify-center"><CheckCircle2 size={32} className="text-[#059669]" /></span>
          <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-[#0F172A]">Authorization complete</h1>
          <p className="text-sm text-[#64748B] mt-2">Thank you, {form.customer_name}. Your authorization of <strong>{money(form.amount, form.currency)}</strong> for {form.merchant_name} has been securely recorded.</p>
          {verId && <p className="mt-4 inline-block rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-mono text-[#475569]">Verification ID: {verId}</p>}
          <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-[#94A3B8]"><Lock size={12} /> Only the last 4 digits of your card were stored.</p>
        </div>
      </Center>
    );
  }

  const terms = form.authorization_terms ||
    `I authorize ${form.merchant_name} to charge my credit card for the amount and schedule indicated above. I understand only the last four digits of my card will be stored. This authorization remains in effect until cancelled in writing.`;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Security bar */}
      <div className="bg-[#0F172A] text-white">
        <div className="max-w-3xl mx-auto px-5 h-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#38BDF8]" />
            <span className="text-sm font-semibold">Secure Authorization</span>
          </div>
          <span className="flex items-center gap-1.5 text-xs text-slate-300"><Lock size={12} /> 256-bit SSL · PCI-minded</span>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-5 py-8">
        <div className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden animate-rise">
          <div className="px-6 sm:px-8 py-6 border-b border-[#E2E8F0]">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{form.business_name || form.merchant_name}</p>
            <h1 className="text-2xl font-extrabold tracking-tight text-[#0F172A] mt-1">Credit Card Authorization</h1>
            <p className="text-sm text-[#64748B] mt-1">Please review and sign to authorize this charge.</p>
          </div>

          {/* Details */}
          <div className="px-6 sm:px-8 py-6 bg-slate-50/60 border-b border-[#E2E8F0]">
            <div className="rounded-lg bg-[#EFF6FF] px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-[#64748B]">Authorized amount</p>
                <p className="text-3xl font-extrabold text-[#2563EB] font-mono">{money(form.amount, form.currency)}</p>
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#2563EB] capitalize">{form.frequency}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-4 text-sm">
              <Info label="Customer" value={form.customer_name} />
              <Info label="Merchant" value={form.merchant_name} />
              {form.frequency_note && <Info label="Schedule" value={form.frequency_note} />}
              {form.description && <Info label="For" value={form.description} />}
            </div>
          </div>

          {/* Card entry */}
          <div className="px-6 sm:px-8 py-6 space-y-4">
            <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2"><CreditCard size={16} /> Card details</h3>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Cardholder name</span>
              <input data-testid="sign-cardholder-input" value={card.cardholder_name} onChange={(e) => setCard({ ...card, cardholder_name: e.target.value })} className="mt-1.5 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm outline-none focus:border-[#2563EB]" placeholder="Name on card" />
            </label>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Card number</span>
              <div className="mt-1.5 relative">
                <input data-testid="sign-card-number-input" inputMode="numeric" value={card.number} onChange={(e) => setCard({ ...card, number: fmtCard(e.target.value) })} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm font-mono outline-none focus:border-[#2563EB] pr-24" placeholder="1234 5678 9012 3456" />
                {brand && <span className="absolute right-2 top-1/2 -translate-y-1/2"><BrandBadge brand={brand} /></span>}
              </div>
            </label>

            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Exp. month</span>
                <select data-testid="sign-exp-month-select" value={card.exp_month} onChange={(e) => setCard({ ...card, exp_month: e.target.value })} className="mt-1.5 w-full rounded-lg border border-[#E2E8F0] px-2 py-2.5 text-sm bg-white outline-none focus:border-[#2563EB]">
                  <option value="">MM</option>
                  {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Exp. year</span>
                <select data-testid="sign-exp-year-select" value={card.exp_year} onChange={(e) => setCard({ ...card, exp_year: e.target.value })} className="mt-1.5 w-full rounded-lg border border-[#E2E8F0] px-2 py-2.5 text-sm bg-white outline-none focus:border-[#2563EB]">
                  <option value="">YY</option>
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Billing ZIP</span>
                <input data-testid="sign-zip-input" value={card.billing_zip} onChange={(e) => setCard({ ...card, billing_zip: e.target.value })} className="mt-1.5 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm font-mono outline-none focus:border-[#2563EB]" placeholder="ZIP" />
              </label>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-[#64748B]"><Lock size={12} /> Your full card number is never sent to or stored on our servers — only the last 4 digits are saved.</p>
          </div>

          {/* Authorization statement */}
          <div className="px-6 sm:px-8 py-5 border-t border-[#E2E8F0]">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Authorization</p>
            <p className="text-sm text-[#334155] leading-relaxed">{terms}</p>
          </div>

          {/* Signature */}
          <div className="px-6 sm:px-8 py-5 border-t border-[#E2E8F0] space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Signature</p>
            <SignaturePad defaultName={form.customer_name} onChange={(type, data) => setSig({ type, data })} />

            <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
              <input data-testid="sign-agree-checkbox" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-[#CBD5E1] text-[#2563EB] focus:ring-[#2563EB]" />
              <span className="text-sm text-[#334155]">I have read and agree to the authorization above, and I confirm I am the authorized cardholder.</span>
            </label>
          </div>

          <div className="px-6 sm:px-8 py-6 border-t border-[#E2E8F0] bg-slate-50/60">
            <button data-testid="signing-submit-btn" onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#059669] py-3 text-sm font-semibold text-white hover:bg-[#047857] transition-all active:scale-[0.99] disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Authorize &amp; sign
            </button>
            <p className="mt-3 text-center text-xs text-[#94A3B8]">By signing you agree this constitutes a legally binding electronic signature.</p>
          </div>
        </div>
      </main>
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-5">{children}</div>
);
const Info = ({ label, value }) => (
  <div>
    <span className="text-xs text-[#94A3B8]">{label}</span>
    <p className="font-semibold text-[#0F172A]">{value}</p>
  </div>
);
